/**
 * Social Media Arche — API surface.
 *
 * All HTTP methods this arche needs live here, callable as
 * `socialMediaApi.<method>(...)`. The methods use the shared transport
 * exposed by the `api` singleton (`api.httpRequest`) rather than living
 * on the singleton itself — so `client/src/lib/api.ts` no longer grows
 * with arche-specific surface area.
 *
 * Per docs/audits/social-media-modularity-audit.md §10 PW-B (social-media
 * slice of the api.ts split). Method names are kept identical to the
 * previous `api.<methodName>()` shape to minimize consumer churn —
 * callers just swap the import and prefix.
 */

import { api } from '@/lib/api';
import type {
  SocialMediaAccountPublic,
  SocialMediaPost,
  AdaptedPlatformContent,
  SocialPlatform,
  PublishAttemptResult,
  SocialMediaPublishLogEntry,
  SocialMediaPostCounts,
  SocialMediaDailyCount,
  SocialAnalyticsRange,
  SocialAnalyticsResponse,
  InspirationPlatform,
  ContentStatus,
  UnifiedContentItem,
  SocialMediaCompetitorItem,
  SocialMediaRssFeedItem,
} from '@/lib/api';

export const socialMediaApi = {
  // ── Accounts ────────────────────────────────────────────────────────

  listSocialMediaAccounts() {
    return api.httpRequest<{ accounts: SocialMediaAccountPublic[] }>(
      'GET',
      '/arches/social-media/accounts',
    );
  },

  initSocialMediaOAuth(platform: string, redirectUri: string) {
    const params = new URLSearchParams({ platform, redirectUri });
    return api.httpRequest<{ authorizationUrl: string; state: string }>(
      'GET',
      `/arches/social-media/accounts/oauth/init?${params.toString()}`,
    );
  },

  disconnectSocialMediaAccount(accountId: string) {
    return api.httpRequest<void>('DELETE', `/arches/social-media/accounts/${accountId}`);
  },

  searchTwitterUsers(query: string, accountId: string, verifiedOnly = false) {
    const params = new URLSearchParams({ query, accountId });
    if (verifiedOnly) params.set('verifiedOnly', 'true');
    return api.httpRequest<{
      users: {
        id: string;
        name: string;
        username: string;
        profileImageUrl?: string;
        description?: string;
        verified: boolean;
        verifiedType: 'blue' | 'government' | 'business' | 'none';
        followersCount: number;
      }[];
    }>('GET', `/arches/social-media/accounts/twitter/users/search?${params.toString()}`);
  },

  searchLinkedInOrgs(query: string, accountId: string) {
    const params = new URLSearchParams({ q: query, accountId });
    return api.httpRequest<{
      orgs: { id: string; name: string; urn: string }[];
    }>('GET', `/arches/social-media/accounts/linkedin/orgs/search?${params.toString()}`);
  },

  // ── Posts ───────────────────────────────────────────────────────────

  listSocialMediaPosts(options?: {
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
    /** ISO 8601 lower bound — COALESCE(publish_at, created_at) >= from */
    from?: string;
    /** ISO 8601 upper bound — COALESCE(publish_at, created_at) <= to */
    to?: string;
  }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.search) params.set('search', options.search);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.from) params.set('from', options.from);
    if (options?.to) params.set('to', options.to);
    const query = params.toString();
    return api.httpRequest<{ posts: SocialMediaPost[]; total: number }>(
      'GET',
      `/arches/social-media/posts${query ? `?${query}` : ''}`,
    );
  },

  createSocialMediaPost(data: {
    originalContent: string;
    targetAccountIds?: string[];
    mediaFileIds?: string[];
    timezone?: string;
    title?: string;
    notes?: string;
    tags?: string[];
    link?: string;
  }) {
    return api.httpRequest<SocialMediaPost>('POST', '/arches/social-media/posts', data);
  },

  getSocialMediaPost(postId: string) {
    return api.httpRequest<SocialMediaPost>('GET', `/arches/social-media/posts/${postId}`);
  },

  updateSocialMediaPost(
    postId: string,
    data: {
      originalContent?: string;
      targetAccountIds?: string[];
      publishAt?: string | null;
      platformContent?: Record<string, AdaptedPlatformContent>;
      mediaFileIds?: string[];
      hasAiContent?: boolean;
      timezone?: string;
      title?: string | null;
      notes?: string | null;
      tags?: string[];
      link?: string | null;
    },
  ) {
    return api.httpRequest<SocialMediaPost>('PATCH', `/arches/social-media/posts/${postId}`, data);
  },

  deleteSocialMediaPost(postId: string) {
    return api.httpRequest<void>('DELETE', `/arches/social-media/posts/${postId}`);
  },

  adaptSocialMediaPost(
    postId: string,
    platforms?: string[],
    context?: {
      tone?: string[];
      goal?: string[];
      audience?: string;
      link?: string;
      currentPlatformText?: Record<string, string>;
    },
    platformSettings?: Record<string, { format?: string }>,
  ) {
    return api.httpRequest<{ platformContent: Record<string, AdaptedPlatformContent> }>(
      'POST',
      `/arches/social-media/posts/${postId}/adapt`,
      { platforms, context, platformSettings },
    );
  },

  enhanceTwitterThread(
    postId: string,
    tweets: string[],
    context?: { tone?: string[]; goal?: string[]; audience?: string },
  ) {
    return api.httpRequest<{ tweets: string[] }>(
      'POST',
      `/arches/social-media/posts/${postId}/enhance-twitter-thread`,
      { tweets, context },
    );
  },

  scheduleSocialMediaPost(postId: string, publishAt: string) {
    return api.httpRequest<SocialMediaPost>(
      'POST',
      `/arches/social-media/posts/${postId}/schedule`,
      { publishAt },
    );
  },

  resetSocialMediaPostToDraft(postId: string) {
    return api.httpRequest<SocialMediaPost>(
      'POST',
      `/arches/social-media/posts/${postId}/reset-draft`,
    );
  },

  publishSocialMediaPostNow(postId: string) {
    return api.httpRequest<{ results: Partial<Record<SocialPlatform, PublishAttemptResult>> }>(
      'POST',
      `/arches/social-media/posts/${postId}/publish-now`,
    );
  },

  getSocialMediaPostLog(postId: string) {
    return api.httpRequest<{ log: SocialMediaPublishLogEntry[] }>(
      'GET',
      `/arches/social-media/posts/${postId}/log`,
    );
  },

  // ── AI helpers ──────────────────────────────────────────────────────

  getSocialMediaSuggestions(content: string, platform?: string) {
    return api.httpRequest<{
      hooks: string[];
      engagementTips: string[];
      toneAnalysis: string;
    }>('POST', '/arches/social-media/ai/suggestions', { content, platform });
  },

  getSocialMediaBestTime(platform: string) {
    return api.httpRequest<{
      recommendations: Array<{ dayOfWeek: string; timeRange: string; reason: string }>;
      generalAdvice: string;
    }>('POST', '/arches/social-media/ai/best-time', { platform });
  },

  getSocialMediaHashtags(content: string, platform: string) {
    return api.httpRequest<{
      recommended: string[];
      trending: string[];
      analysis: string;
    }>('POST', '/arches/social-media/ai/hashtags', { content, platform });
  },

  enhanceImagePrompt(prompt: string) {
    return api.httpRequest<{
      enhanced: {
        fields: Array<{ label: string; value: string }>;
        clarification?: string;
      };
    }>('POST', '/arches/social-media/ai/enhance-image-prompt', { prompt });
  },

  generateWriterIdeas(data: {
    direction: string;
    directionOverride?: string;
    audiencePain?: string[];
    audiencePainOverride?: string;
    style?: string[];
    hookPreference?: string[];
    inspirationSeed?: string;
    selectedInspirationIds?: string[];
    selectedInspirationContent?: Array<{ author: string; content: string; platform: string }>;
    constraints?: string;
    feedback?: string;
  }) {
    return api.httpRequest<{
      ideas: Array<{
        hook: string;
        concept: string;
        angle: string;
        format: 'tweet' | 'thread' | 'carousel' | 'video';
        notes?: string;
        whyItWorks: string;
        pillar: string;
      }>;
    }>('POST', '/arches/social-media/ai/writer/ideas', data);
  },

  regenerateWriterIdea(data: {
    direction: string;
    pillar: string;
    audiencePain?: string[];
    style?: string[];
    hookPreference?: string[];
    constraints?: string;
  }) {
    return api.httpRequest<{
      idea: {
        hook: string;
        concept: string;
        angle: string;
        format: 'tweet' | 'thread' | 'carousel' | 'video';
        notes?: string;
        whyItWorks: string;
        pillar: string;
      };
    }>('POST', '/arches/social-media/ai/writer/regenerate-idea', data);
  },

  generateWriterCalendar(data: {
    timeAmount: number;
    timeUnit: 'weeks' | 'months';
    scheduleType: 'per-period' | 'daily';
    postsPerPeriod: number;
    postsPerDay: number;
    platforms?: string[];
    seedIdeas?: string;
    selectedInspirationContent?: Array<{ author: string; content: string; platform: string }>;
    goal?: string;
    contentStyle?: string[];
    engagementStyle?: string[];
    constraints?: string;
    pillars?: Array<{ name: string; weight: number }>;
  }) {
    return api.httpRequest<{
      posts: Array<{
        day: string;
        time: string;
        pillar: string;
        hook: string;
        copy: string;
        format: string;
        formatHint: string;
      }>;
    }>('POST', '/arches/social-media/ai/writer/calendar', data);
  },

  // ── Stats + analytics ───────────────────────────────────────────────

  getSocialMediaStats(month?: string) {
    const params = month ? `?month=${encodeURIComponent(month)}` : '';
    return api.httpRequest<{
      counts: SocialMediaPostCounts;
      dailyActivity: SocialMediaDailyCount[];
    }>('GET', `/arches/social-media/stats${params}`);
  },

  getSocialMediaAnalytics(params: { range: SocialAnalyticsRange; platform?: SocialPlatform }) {
    const qs = new URLSearchParams({ range: params.range });
    if (params.platform) qs.set('platform', params.platform);
    return api.httpRequest<SocialAnalyticsResponse>(
      'GET',
      `/arches/social-media/analytics?${qs.toString()}`,
    );
  },

  // ── Inspiration content ─────────────────────────────────────────────

  listInspirationContent(options?: {
    platform?: InspirationPlatform;
    status?: ContentStatus;
    search?: string;
    sortBy?: 'engagement' | 'fetched' | 'posted';
    sortDir?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
    includeDiscarded?: boolean;
  }) {
    const params = new URLSearchParams();
    if (options?.platform) params.set('platform', options.platform);
    if (options?.status) params.set('status', options.status);
    if (options?.search) params.set('search', options.search);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortDir) params.set('sortDir', options.sortDir);
    if (options?.limit !== undefined) params.set('limit', String(options.limit));
    if (options?.offset !== undefined) params.set('offset', String(options.offset));
    if (options?.includeDiscarded) params.set('includeDiscarded', 'true');
    const qs = params.toString();
    return api.httpRequest<{ items: UnifiedContentItem[]; total: number }>(
      'GET',
      `/arches/social-media/inspiration${qs ? `?${qs}` : ''}`,
    );
  },

  updateInspirationContent(
    contentId: string,
    update: { status?: ContentStatus; isFavorited?: boolean; pillarTag?: string | null },
  ) {
    return api.httpRequest<{ item: UnifiedContentItem }>(
      'PATCH',
      `/arches/social-media/inspiration/${contentId}`,
      update,
    );
  },

  bulkUpdateInspirationContent(data: {
    contentIds: string[];
    action: 'status' | 'favorite' | 'unfavorite' | 'tag' | 'discard';
    status?: ContentStatus;
    pillarTag?: string | null;
  }) {
    return api.httpRequest<{ updatedCount: number }>(
      'POST',
      '/arches/social-media/inspiration/bulk',
      data,
    );
  },

  // ── Inspiration sources (competitors + RSS + manual) ────────────────

  listCompetitors() {
    return api.httpRequest<{ competitors: SocialMediaCompetitorItem[] }>(
      'GET',
      '/arches/social-media/inspiration/sources/competitors',
    );
  },

  addCompetitor(data: { platform: InspirationPlatform; username: string; displayName?: string }) {
    return api.httpRequest<{ competitor: SocialMediaCompetitorItem }>(
      'POST',
      '/arches/social-media/inspiration/sources/competitors',
      data,
    );
  },

  deleteCompetitor(competitorId: string) {
    return api.httpRequest<undefined>(
      'DELETE',
      `/arches/social-media/inspiration/sources/competitors/${competitorId}`,
    );
  },

  triggerCompetitorFetch(competitorId: string) {
    return api.httpRequest<{ competitor: SocialMediaCompetitorItem; message: string }>(
      'POST',
      `/arches/social-media/inspiration/sources/competitors/${competitorId}/fetch`,
    );
  },

  abortCompetitorFetch(competitorId: string) {
    return api.httpRequest<{ competitor: SocialMediaCompetitorItem }>(
      'DELETE',
      `/arches/social-media/inspiration/sources/competitors/${competitorId}/fetch`,
    );
  },

  listRssFeeds() {
    return api.httpRequest<{ feeds: SocialMediaRssFeedItem[] }>(
      'GET',
      '/arches/social-media/inspiration/sources/rss',
    );
  },

  addRssFeed(data: { title: string; url: string }) {
    return api.httpRequest<{ feed: SocialMediaRssFeedItem }>(
      'POST',
      '/arches/social-media/inspiration/sources/rss',
      data,
    );
  },

  deleteRssFeed(feedId: string) {
    return api.httpRequest<undefined>(
      'DELETE',
      `/arches/social-media/inspiration/sources/rss/${feedId}`,
    );
  },

  triggerRssFeedFetch(feedId: string) {
    return api.httpRequest<{ feed: SocialMediaRssFeedItem; message: string }>(
      'POST',
      `/arches/social-media/inspiration/sources/rss/${feedId}/fetch`,
    );
  },

  fetchManualInspirationUrl(url: string) {
    const encoded = encodeURIComponent(url);
    return api.httpRequest<{ text: string }>(
      'GET',
      `/arches/social-media/inspiration/sources/manual/fetch-url?url=${encoded}`,
    );
  },

  addManualInspiration(data: { text: string; title?: string; sourceUrl?: string }) {
    return api.httpRequest<{ contentId: string; message: string }>(
      'POST',
      '/arches/social-media/inspiration/sources/manual',
      data,
    );
  },

  generatePostDraft(contentId: string) {
    return api.httpRequest<{ postId: string }>(
      'POST',
      `/arches/social-media/inspiration/${contentId}/generate-draft`,
    );
  },

  // ── Brand settings ──────────────────────────────────────────────────

  getBrandSettings() {
    return api.httpRequest<{
      settings: {
        settingId: string | null;
        userId: string;
        identity: {
          brandName: string;
          industry: string | null;
          tagline: string | null;
          website: string | null;
        };
        logo: {
          lightLogoFileId: string | null;
          darkLogoFileId: string | null;
          usage: string;
          placement: string;
        };
        palette: Array<{ name: string; hex: string }>;
        voice: {
          personaArchetype: string;
          toneSpectrum: { formality: number; humor: number; boldness: number; empathy: number };
          voiceDescriptors: string[];
          avoidDescriptors: string[];
          brandDescription: string | null;
          targetAudience: string | null;
          contentPillars: string | null;
          personaVoice: string | null;
          signOffStyle: string;
          defaultCTAType: string;
          wordsToUse: string | null;
          wordsToNeverUse: string | null;
          brandSpecificTerms: string | null;
          hashtagStrategy: string | null;
          emojiUsage: string;
          postLengthPreference: string;
          useLineBreaks: boolean;
          includeQuestions: boolean;
          referenceBrandColor: boolean;
        };
        platformOverrides: Array<{
          platform: string;
          toneNote: string | null;
          isActive: boolean;
        }>;
        createdAt: string | null;
        updatedAt: string | null;
      };
    }>('GET', '/arches/social-media/brand-settings');
  },

  upsertBrandSettings(data: {
    identity?: Partial<{
      brandName: string;
      industry: string | null;
      tagline: string | null;
      website: string | null;
    }>;
    logo?: Partial<{
      lightLogoFileId: string | null;
      darkLogoFileId: string | null;
      usage: string;
      placement: string;
    }>;
    palette?: Array<{ name: string; hex: string }>;
    voice?: Partial<{
      personaArchetype: string;
      toneSpectrum: { formality: number; humor: number; boldness: number; empathy: number };
      voiceDescriptors: string[];
      avoidDescriptors: string[];
      brandDescription: string | null;
      targetAudience: string | null;
      contentPillars: string | null;
      personaVoice: string | null;
      signOffStyle: string;
      defaultCTAType: string;
      wordsToUse: string | null;
      wordsToNeverUse: string | null;
      brandSpecificTerms: string | null;
      hashtagStrategy: string | null;
      emojiUsage: string;
      postLengthPreference: string;
      useLineBreaks: boolean;
      includeQuestions: boolean;
      referenceBrandColor: boolean;
    }>;
    platformOverrides?: Array<{
      platform: string;
      toneNote: string | null;
      isActive: boolean;
    }>;
  }) {
    return api.httpRequest<{
      settings: {
        settingId: string | null;
        userId: string;
        identity: {
          brandName: string;
          industry: string | null;
          tagline: string | null;
          website: string | null;
        };
        logo: {
          lightLogoFileId: string | null;
          darkLogoFileId: string | null;
          usage: string;
          placement: string;
        };
        palette: Array<{ name: string; hex: string }>;
        voice: {
          personaArchetype: string;
          toneSpectrum: { formality: number; humor: number; boldness: number; empathy: number };
          voiceDescriptors: string[];
          avoidDescriptors: string[];
          brandDescription: string | null;
          targetAudience: string | null;
          contentPillars: string | null;
          personaVoice: string | null;
          signOffStyle: string;
          defaultCTAType: string;
          wordsToUse: string | null;
          wordsToNeverUse: string | null;
          brandSpecificTerms: string | null;
          hashtagStrategy: string | null;
          emojiUsage: string;
          postLengthPreference: string;
          useLineBreaks: boolean;
          includeQuestions: boolean;
          referenceBrandColor: boolean;
        };
        platformOverrides: Array<{
          platform: string;
          toneNote: string | null;
          isActive: boolean;
        }>;
        createdAt: string | null;
        updatedAt: string | null;
      };
    }>('PUT', '/arches/social-media/brand-settings', data);
  },
};
